import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import PhygitalIcon from "@/components/PhygitalIcon";
import {
  Lock,
  ChevronRight,
  Check,
  Play,
  Eye,
  Send,
  Calendar,
  Users,
  Sparkles,
  ArrowRight,
  GraduationCap,
  Building,
  MessageSquare,
  ChevronDown,
  ExternalLink,
  Rocket,
  BookOpen,
  Lightbulb,
  Target,
  Clock,
  DollarSign,
  Users2,
  Zap,
  FileText,
  Link as LinkIcon,
  CheckCircle,
  Circle,
  ArrowUpRight,
  Menu,
  X,
  Copy,
  Code,
  Building2,
  Briefcase,
  Heart,
  Globe,
  Award
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Phygital Brand Colors
const BRAND_COLORS = {
  black: "#000000",
  blue: "#2563eb",
  pink: "#db2777",
  orange: "#f67315",
  purple: "#9332ea",
  green: "#27803d",
  teal: "#2e9488",
};

// Front Row Icon - Binoculars/VIP view style
const FrontRowIcon = ({ className = "w-6 h-6" }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    <circle cx="7" cy="12" r="4" stroke="currentColor" strokeWidth="2"/>
    <circle cx="17" cy="12" r="4" stroke="currentColor" strokeWidth="2"/>
    <path d="M11 12H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M7 8V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M17 8V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <circle cx="7" cy="12" r="1.5" fill="currentColor" opacity="0.3"/>
    <circle cx="17" cy="12" r="1.5" fill="currentColor" opacity="0.3"/>
  </svg>
);

// Tool/Resource card with app-like design
const ToolCard = ({ name, icon, url, description, affiliateCode, affiliateDiscount }) => {
  const [copied, setCopied] = useState(false);
  
  const copyCode = () => {
    if (affiliateCode) {
      navigator.clipboard.writeText(affiliateCode);
      setCopied(true);
      toast.success(`Code "${affiliateCode}" copied!`);
      setTimeout(() => setCopied(false), 2000);
    }
  };
  
  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm">
      <div className="flex items-start gap-3">
        {/* App icon style */}
        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-gray-100 to-gray-50 flex items-center justify-center flex-shrink-0 overflow-hidden">
          {icon ? (
            <img src={icon} alt={name} className="w-10 h-10 object-contain" />
          ) : (
            <div className="w-10 h-10 bg-black rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-lg">{name.charAt(0)}</span>
            </div>
          )}
        </div>
        
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-gray-900 text-sm">{name}</h4>
          <p className="text-xs text-gray-500 line-clamp-2 mt-0.5">{description}</p>
          
          {affiliateCode && (
            <div className="mt-2 flex items-center gap-2">
              <button
                onClick={copyCode}
                className="flex items-center gap-1 px-2 py-1 bg-pink-50 text-pink-600 rounded-lg text-xs font-medium hover:bg-pink-100 transition-colors"
              >
                {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                {affiliateDiscount} - {affiliateCode}
              </button>
            </div>
          )}
        </div>
        
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="w-8 h-8 bg-black rounded-full flex items-center justify-center flex-shrink-0 hover:bg-gray-800 transition-colors"
        >
          <ArrowUpRight className="w-4 h-4 text-white" />
        </a>
      </div>
    </div>
  );
};

// January content with tools
const JANUARY_CONTENT = {
  month_number: 1,
  title: "January 2026",
  subtitle: "From nail salon to tech startup",
  timeline: [
    { date: "Jan 1-5", event: "Ideated phygital from a nail visit", type: "discovery" },
    { date: "Jan 6-10", event: "Market research on Perplexity", type: "research" },
    { date: "Jan 11-15", event: "Built MVP with AI tools", type: "build" },
    { date: "Jan 16-20", event: "Brand identity + legal setup", type: "business" },
    { date: "Jan 21-25", event: "User interviews (hair, MUA)", type: "research" },
    { date: "Jan 26-31", event: "Secured 3 pilots + launched waitlist", type: "launch" },
  ],
  sections: {
    what_i_did: [
      { text: "Ideated phygital from a nail visit", detail: "Identified the problem of repeating preferences" },
      { text: "Researched market & integrations", detail: "Used Perplexity for deep research" },
      { text: "Built MVP with AI tools", detail: "Full-stack app in days, not months" },
      { text: "Brand identity", detail: "Logo + color scheme on Canva" },
      { text: "Interviewed hair stylists & MUAs", detail: "Real user feedback" },
      { text: "Set up legal", detail: "LLC formation" },
      { text: "Pricing & GTM strategy" },
      { text: "Secured 3 pilots", detail: "Hair, MUA, Events (fashion x tech)" },
      { text: "Created business plan" },
      { text: "Notion HQ setup" },
      { text: "Personal brand website", detail: "Community platform" },
      { text: "Content operations setup" },
      { text: "Waitlist + approval system" },
      { text: "Admin dashboard" },
      { text: "Beehiiv email API integration" },
      { text: "Instagram DM research" },
    ],
    what_i_used: [
      { 
        name: "AI Dev Platform", 
        icon: "https://img.icons8.com/fluency/48/artificial-intelligence.png",
        url: "#", 
        description: "AI-powered development platform. Built the entire MVP here.",
        affiliateCode: "",
        affiliateDiscount: ""
      },
      { 
        name: "Perplexity", 
        icon: "https://www.perplexity.ai/favicon.ico",
        url: "https://perplexity.ai", 
        description: "AI research assistant for market analysis"
      },
      { 
        name: "Notion", 
        icon: "https://www.notion.so/images/favicon.ico",
        url: "https://notion.so", 
        description: "Documentation & company HQ"
      },
      { 
        name: "Beehiiv", 
        icon: "https://www.beehiiv.com/favicon.ico",
        url: "https://beehiiv.com", 
        description: "Email marketing & waitlist management"
      },
      { 
        name: "Canva", 
        icon: "https://www.canva.com/favicon.ico",
        url: "https://canva.com", 
        description: "Brand design & visual assets"
      },
    ],
    what_i_learned: [
      "Interview users ASAP - hair stylist had different needs than expected, MUA had more ideas",
      "Start small then build - focus on solo user type first, then expand to teams",
      "Document everything - solid structure helps organizing things, systems easier to maintain"
    ],
    whats_next: [
      "Set up bank account + Stripe",
      "Take more interviews + improve product",
      "Special invite to pilot testers",
      "Content moderation tools",
      "Acuity API integration",
      "Strategize team building"
    ],
  },
  feedback_questions: [
    "What was helpful for you?",
    "What are you still confused about?",
    "What do you want to know more of?"
  ]
};

// Guide/Founder Info
const GUIDE_INFO = {
  name: "Your Host",
  title: "Event Organizer",
  subtitle: "Event Host",
  bio: "Creating meaningful connections through curated experiences and innovative event technology.",
  photo: "https://images.unsplash.com/photo-1710778044102-56a3a6b69a1b?w=300&h=300&fit=crop&crop=face",
  journey: [
    { age: "Vision", milestone: "Event innovation", icon: Code },
    { age: "Community", milestone: "Building connections", icon: Building2 },
    { age: "Future", milestone: "Growing together", icon: Rocket },
  ],
  perspectives: [
    {
      title: "Technology Enhances Connection",
      text: "Technology removes barriers. We use innovative tools to create meaningful connections between attendees, fostering relationships that last beyond the event."
    },
    {
      title: "Community-Centered Design",
      text: "Every event is designed with the community in mind. We create spaces where people can authentically connect, share ideas, and build together."
    }
  ]
};

// Provider Profile Section Component - Editable Intake Responses
const ProviderProfileSection = ({ studentData, onUpdate }) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [profileData, setProfileData] = useState({
    first_name: "",
    last_name: "",
    business_name: "",
    email: "",
    phone: "",
    location: "",
    instagram: "",
    tiktok: "",
    website: "",
    booking_platforms: [],
    payment_methods: [],
    services_offered: "",
    years_experience: "",
    ...studentData?.intake_responses
  });

  useEffect(() => {
    if (studentData) {
      setProfileData(prev => ({
        ...prev,
        first_name: studentData.first_name || studentData.name?.split(" ")[0] || "",
        last_name: studentData.last_name || studentData.name?.split(" ").slice(1).join(" ") || "",
        business_name: studentData.business_name || "",
        email: studentData.email || "",
        phone: studentData.phone || "",
        location: studentData.city || studentData.location || "",
        ...studentData.intake_responses
      }));
    }
  }, [studentData]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("frontrow_token") || localStorage.getItem("session_token");
      const res = await fetch(`${API}/provider/profile/update`, {
        method: "PUT",
        headers: { 
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(profileData)
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success("Profile updated!");
        onUpdate?.(data);
        setEditing(false);
      } else {
        toast.error("Failed to update profile");
      }
    } catch (err) {
      toast.error("Error saving profile");
    }
    setSaving(false);
  };

  const BOOKING_OPTIONS = ["Acuity", "Calendly", "Square", "Vagaro", "Fresha", "StyleSeat", "Booksy", "Mindbody", "DMs", "Text/Phone"];
  const PAYMENT_OPTIONS = ["Venmo", "CashApp", "Zelle", "Square", "Stripe", "PayPal", "Cash", "Card"];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-lg text-gray-900">My Profile</h3>
        <Button
          onClick={() => editing ? handleSave() : setEditing(true)}
          disabled={saving}
          size="sm"
          className={editing ? "bg-green-600 hover:bg-green-700" : ""}
        >
          {saving ? "Saving..." : editing ? "Save Changes" : "Edit Profile"}
        </Button>
      </div>
      
      {/* Basic Info */}
      <Card className="rounded-2xl">
        <CardContent className="p-4 space-y-3">
          <h4 className="font-semibold text-sm text-gray-700 flex items-center gap-2">
            <Users2 className="w-4 h-4" />
            Basic Information
          </h4>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-500">First Name</label>
              <Input
                value={profileData.first_name || ""}
                onChange={(e) => setProfileData(prev => ({ ...prev, first_name: e.target.value }))}
                disabled={!editing}
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500">Last Name</label>
              <Input
                value={profileData.last_name || ""}
                onChange={(e) => setProfileData(prev => ({ ...prev, last_name: e.target.value }))}
                disabled={!editing}
                className="mt-1"
              />
            </div>
            <div className="col-span-2">
              <label className="text-xs text-gray-500">Business Name</label>
              <Input
                value={profileData.business_name || ""}
                onChange={(e) => setProfileData(prev => ({ ...prev, business_name: e.target.value }))}
                disabled={!editing}
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500">Email</label>
              <Input
                value={profileData.email || ""}
                disabled={true}
                className="mt-1 bg-gray-50"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500">Phone</label>
              <Input
                value={profileData.phone || ""}
                onChange={(e) => setProfileData(prev => ({ ...prev, phone: e.target.value }))}
                disabled={!editing}
                className="mt-1"
              />
            </div>
            <div className="col-span-2">
              <label className="text-xs text-gray-500">Location</label>
              <Input
                value={profileData.location || ""}
                onChange={(e) => setProfileData(prev => ({ ...prev, location: e.target.value }))}
                disabled={!editing}
                placeholder="City, State"
                className="mt-1"
              />
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Social Media */}
      <Card className="rounded-2xl">
        <CardContent className="p-4 space-y-3">
          <h4 className="font-semibold text-sm text-gray-700 flex items-center gap-2">
            <Globe className="w-4 h-4" />
            Social & Web Presence
          </h4>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-500">Instagram</label>
              <Input
                value={profileData.instagram || ""}
                onChange={(e) => setProfileData(prev => ({ ...prev, instagram: e.target.value }))}
                disabled={!editing}
                placeholder="@handle"
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500">TikTok</label>
              <Input
                value={profileData.tiktok || ""}
                onChange={(e) => setProfileData(prev => ({ ...prev, tiktok: e.target.value }))}
                disabled={!editing}
                placeholder="@handle"
                className="mt-1"
              />
            </div>
            <div className="col-span-2">
              <label className="text-xs text-gray-500">Website</label>
              <Input
                value={profileData.website || ""}
                onChange={(e) => setProfileData(prev => ({ ...prev, website: e.target.value }))}
                disabled={!editing}
                placeholder="yourwebsite.com"
                className="mt-1"
              />
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Booking & Payments */}
      <Card className="rounded-2xl">
        <CardContent className="p-4 space-y-3">
          <h4 className="font-semibold text-sm text-gray-700 flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Booking & Payments
          </h4>
          
          <div>
            <label className="text-xs text-gray-500 block mb-2">Booking Platforms</label>
            <div className="flex flex-wrap gap-2">
              {BOOKING_OPTIONS.map(option => {
                const isSelected = (profileData.booking_platforms || []).includes(option);
                return (
                  <button
                    key={option}
                    type="button"
                    disabled={!editing}
                    onClick={() => {
                      const current = profileData.booking_platforms || [];
                      const updated = isSelected 
                        ? current.filter(p => p !== option)
                        : [...current, option];
                      setProfileData(prev => ({ ...prev, booking_platforms: updated }));
                    }}
                    className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                      isSelected 
                        ? "bg-purple-600 text-white border-purple-600" 
                        : "bg-white text-gray-600 border-gray-300"
                    } ${!editing ? "opacity-60" : "hover:border-purple-400"}`}
                  >
                    {option}
                  </button>
                );
              })}
            </div>
          </div>
          
          <div>
            <label className="text-xs text-gray-500 block mb-2">Payment Methods</label>
            <div className="flex flex-wrap gap-2">
              {PAYMENT_OPTIONS.map(option => {
                const isSelected = (profileData.payment_methods || []).includes(option);
                return (
                  <button
                    key={option}
                    type="button"
                    disabled={!editing}
                    onClick={() => {
                      const current = profileData.payment_methods || [];
                      const updated = isSelected 
                        ? current.filter(p => p !== option)
                        : [...current, option];
                      setProfileData(prev => ({ ...prev, payment_methods: updated }));
                    }}
                    className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                      isSelected 
                        ? "bg-green-600 text-white border-green-600" 
                        : "bg-white text-gray-600 border-gray-300"
                    } ${!editing ? "opacity-60" : "hover:border-green-400"}`}
                  >
                    {option}
                  </button>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Services */}
      <Card className="rounded-2xl">
        <CardContent className="p-4 space-y-3">
          <h4 className="font-semibold text-sm text-gray-700 flex items-center gap-2">
            <Sparkles className="w-4 h-4" />
            Services & Experience
          </h4>
          <div>
            <label className="text-xs text-gray-500">Services Offered</label>
            <Textarea
              value={profileData.services_offered || ""}
              onChange={(e) => setProfileData(prev => ({ ...prev, services_offered: e.target.value }))}
              disabled={!editing}
              placeholder="Describe your services..."
              rows={3}
              className="mt-1"
            />
          </div>
          <div>
            <label className="text-xs text-gray-500">Years of Experience</label>
            <Input
              value={profileData.years_experience || ""}
              onChange={(e) => setProfileData(prev => ({ ...prev, years_experience: e.target.value }))}
              disabled={!editing}
              placeholder="5"
              className="mt-1"
            />
          </div>
        </CardContent>
      </Card>
      
      {editing && (
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setEditing(false)}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 bg-green-600 hover:bg-green-700"
          >
            {saving ? "Saving..." : "Save All Changes"}
          </Button>
        </div>
      )}
    </div>
  );
};

const FrontRowPage = () => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [isStudent, setIsStudent] = useState(false);
  const [studentData, setStudentData] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(1);
  const [feedback, setFeedback] = useState("");
  const [takeaways, setTakeaways] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showSignup, setShowSignup] = useState(false);
  const [activeSection, setActiveSection] = useState("what_i_did");
  const [mobileNav, setMobileNav] = useState(false);
  const [expandedGuide, setExpandedGuide] = useState(false);
  const [feedbackAttachments, setFeedbackAttachments] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);

  useEffect(() => {
    const studentToken = localStorage.getItem("frontrow_token");
    if (studentToken) {
      fetchStudentData(studentToken);
    }
  }, []);

  const fetchStudentData = async (token) => {
    try {
      const res = await fetch(`${API}/frontrow/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setStudentData(data);
        setIsStudent(true);
      }
    } catch (err) {
      console.error("Error:", err);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    if (!email || !name) {
      toast.error("Please enter your name and email");
      return;
    }
    
    try {
      const res = await fetch(`${API}/frontrow/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, name })
      });
      
      if (res.ok) {
        const data = await res.json();
        localStorage.setItem("frontrow_token", data.token);
        setStudentData(data.student);
        setIsStudent(true);
        toast.success("Welcome to Front Row!");
      }
    } catch (err) {
      toast.error("Something went wrong");
    }
  };

  const handleFeedbackFileUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    setUploadingFile(true);
    const token = localStorage.getItem("frontrow_token");
    const uploadedFiles = [];
    
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`File ${file.name} is too large. Max 10MB.`);
        continue;
      }
      
      const formData = new FormData();
      formData.append("file", file);
      
      try {
        const res = await fetch(`${API}/feedback/upload-attachment`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData
        });
        
        if (res.ok) {
          const data = await res.json();
          uploadedFiles.push({
            attachment_id: data.attachment_id,
            filename: file.name,
            url: data.url,
            type: file.type
          });
        } else {
          toast.error(`Failed to upload ${file.name}`);
        }
      } catch (err) {
        toast.error(`Error uploading ${file.name}`);
      }
    }
    
    if (uploadedFiles.length > 0) {
      setFeedbackAttachments(prev => [...prev, ...uploadedFiles]);
      toast.success(`${uploadedFiles.length} file(s) uploaded!`);
    }
    setUploadingFile(false);
    e.target.value = "";
  };

  const removeFeedbackAttachment = (attachmentId) => {
    setFeedbackAttachments(prev => prev.filter(a => a.attachment_id !== attachmentId));
  };

  const handleSubmitFeedback = async () => {
    if (!feedback.trim()) {
      toast.error("Please provide your feedback");
      return;
    }
    
    setSubmitting(true);
    try {
      const token = localStorage.getItem("frontrow_token");
      const res = await fetch(`${API}/frontrow/submit`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          month_number: currentMonth,
          feedback,
          takeaways: takeaways || "N/A",
          attachments: feedbackAttachments.map(a => a.attachment_id)
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success("Feedback submitted! Next month unlocked 🔓");
        setStudentData(data.student);
        setFeedback("");
        setTakeaways("");
        setFeedbackAttachments([]);
      }
    } catch (err) {
      toast.error("Failed to submit");
    }
    setSubmitting(false);
  };

  const content = JANUARY_CONTENT;

  // Mobile-first landing page
  if (!isStudent) {
    return (
      <div className="min-h-screen bg-white">
        {/* Mobile Nav */}
        <nav className="sticky top-0 z-50 bg-white border-b border-gray-100">
          <div className="px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 bg-black rounded-xl flex items-center justify-center">
                <FrontRowIcon className="w-5 h-5 text-white" />
              </div>
              <div>
                <span className="font-bold text-gray-900 text-sm">Front Row</span>
                <div className="flex items-center gap-1 text-[10px] text-gray-400">
                  <span>powered by</span>
                  <PhygitalIcon className="w-3 h-3" />
                </div>
              </div>
            </div>
            <Link to="/" className="text-xs text-gray-400 hover:text-gray-600">
              phygital.systems
            </Link>
          </div>
        </nav>

        {/* Hero - Mobile First */}
        <div className="px-6 py-12 max-w-lg mx-auto">
          <Badge className="bg-gray-100 text-gray-700 border-0 mb-6 mx-auto block w-fit text-xs">
            For Students and Builders
          </Badge>
          
          <h1 className="text-3xl md:text-4xl font-bold mb-6 text-center text-gray-900 leading-tight">
            Get a <span className="text-pink-500">Front Row Seat</span> to Building a Tech Startup
          </h1>
          
          <p className="text-gray-600 text-center mb-3 text-base leading-relaxed">
            Watch someone launch a startup from the ground up, every step, play by play.
          </p>
          
          <p className="text-gray-500 text-center mb-10 text-sm">
            Not a blueprint to copy, but a real-time guide to learn from.
          </p>
          
          {/* Phygital Logo */}
          <div className="flex justify-center mb-10">
            <PhygitalIcon className="w-14 h-14 text-gray-200" />
          </div>
          
          {!showSignup ? (
            <Button 
              onClick={() => setShowSignup(true)}
              className="w-full bg-black hover:bg-gray-800 text-white font-semibold py-6 rounded-2xl"
              data-testid="get-frontrow-seat-btn"
            >
              <FrontRowIcon className="w-5 h-5 mr-2" />
              Get Your Front Row Seat
            </Button>
          ) : (
            <form onSubmit={handleSignup} className="space-y-4">
              <Input
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="h-12 rounded-xl border-gray-200"
                data-testid="frontrow-name-input"
              />
              <Input
                type="email"
                placeholder="Your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12 rounded-xl border-gray-200"
                data-testid="frontrow-email-input"
              />
              <Button type="submit" className="w-full bg-black hover:bg-gray-800 h-12 rounded-xl" data-testid="frontrow-signup-btn">
                Start Learning <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </form>
          )}
        </div>
        
        {/* About Front Row Section */}
        <div className="bg-gray-50 px-6 py-12">
          <div className="max-w-lg mx-auto">
            <h2 className="text-xl font-bold text-center mb-3 text-gray-900">About Front Row</h2>
            <p className="text-sm text-gray-600 text-center mb-8 leading-relaxed">
              A month-by-month documentary of building Phygital from idea to launch. Real decisions, real mistakes, real lessons.
            </p>
            
            <div className="space-y-4">
              {[
                { icon: Eye, title: "Watch", desc: "Monthly progress updates", color: "bg-blue-100 text-blue-600" },
                { icon: BookOpen, title: "Learn", desc: "Tools, resources, lessons", color: "bg-pink-100 text-pink-600" },
                { icon: MessageSquare, title: "Reflect", desc: "Submit your feedback", color: "bg-purple-100 text-purple-600" },
                { icon: Lock, title: "Unlock", desc: "Access next month", color: "bg-green-100 text-green-600" }
              ].map((step, idx) => (
                <div key={idx} className="flex items-center gap-4 bg-white p-4 rounded-2xl shadow-sm">
                  <div className={`w-12 h-12 ${step.color} rounded-xl flex items-center justify-center`}>
                    <step.icon className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{step.title}</h3>
                    <p className="text-sm text-gray-500">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        {/* Meet Your Guide Section */}
        <div className="px-6 py-12 bg-white">
          <div className="max-w-lg mx-auto">
            <h2 className="text-xl font-bold text-center mb-2 text-gray-900">Meet Your Guide</h2>
            <p className="text-xs text-gray-400 text-center mb-8 uppercase tracking-wider">
              Curated by Ocean AI
            </p>
            
            <Card className="border-0 shadow-lg rounded-2xl overflow-hidden">
              <CardContent className="p-0">
                {/* Guide Header */}
                <div className="bg-gradient-to-br from-gray-900 to-gray-800 p-6 text-white">
                  <div className="flex items-center gap-4">
                    <div className="w-20 h-20 rounded-2xl overflow-hidden border-2 border-white/20">
                      <img 
                        src={GUIDE_INFO.photo} 
                        alt={GUIDE_INFO.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold">{GUIDE_INFO.name}</h3>
                      <p className="text-pink-400 text-sm font-medium">{GUIDE_INFO.subtitle}</p>
                    </div>
                  </div>
                  <p className="text-gray-300 text-sm mt-4 leading-relaxed">{GUIDE_INFO.bio}</p>
                </div>
                
                {/* My Journey */}
                <div className="p-6 border-b border-gray-100">
                  <h4 className="text-sm font-semibold text-gray-900 mb-4">My Journey</h4>
                  <div className="flex gap-3">
                    {GUIDE_INFO.journey.map((item, idx) => {
                      const JourneyIcon = item.icon;
                      return (
                        <div key={idx} className="flex-1 bg-gray-50 rounded-xl p-3 text-center">
                          <div className="w-10 h-10 mx-auto mb-2 bg-black rounded-lg flex items-center justify-center">
                            <JourneyIcon className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-xs text-gray-500 mb-1">{item.age}</p>
                          <p className="text-xs font-medium text-gray-900">{item.milestone}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                {/* My Perspective on AI */}
                <div className="p-6">
                  <h4 className="text-sm font-semibold text-gray-900 mb-4">My Perspective on AI</h4>
                  <div className="space-y-3">
                    {GUIDE_INFO.perspectives.map((item, idx) => (
                      <div key={idx} className={`p-4 rounded-xl ${idx === 0 ? 'bg-blue-50 border-l-4 border-blue-500' : 'bg-pink-50 border-l-4 border-pink-500'}`}>
                        <h5 className="font-semibold text-gray-900 text-sm mb-2">{item.title}</h5>
                        <p className="text-xs text-gray-600 leading-relaxed">{item.text}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
        
        {/* What I Used - Affiliate Section */}
        <div className="bg-gray-50 px-6 py-12">
          <div className="max-w-lg mx-auto">
            <h2 className="text-xl font-bold text-center mb-3 text-gray-900">What I Used to Build This</h2>
            <p className="text-sm text-gray-500 text-center mb-8 leading-relaxed">
              Tools I actually use. Affiliate codes where I have them. You get a discount, I get credit. Win-win.
            </p>
            
            <div className="space-y-4">
              {/* AI Dev Platform - Featured */}
              <div className="bg-gradient-to-r from-black to-gray-800 rounded-2xl p-5 text-white">
                <div className="flex items-center gap-3 mb-4">
                  <img 
                    src="https://img.icons8.com/fluency/48/artificial-intelligence.png" 
                    alt="AI Dev Tools" 
                    className="w-12 h-12 rounded-xl bg-white p-1"
                  />
                  <div className="flex-1">
                    <h4 className="font-bold">AI Dev Tools</h4>
                    <p className="text-xs text-gray-300">AI-powered development platform</p>
                  </div>
                </div>
                <p className="text-sm text-gray-300 mb-4 leading-relaxed">
                  Built my entire MVP here. From zero to full-stack app in days, not months.
                </p>
                <div className="flex gap-2">
                  <a 
                    href="#"
                    className="flex-1 bg-white text-black text-center py-2.5 rounded-xl text-sm font-medium hover:bg-gray-100 transition-colors"
                  >
                    Learn More
                  </a>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText("NAOMI");
                      toast.success("Code NAOMI copied!");
                    }}
                    className="px-4 py-2.5 bg-white/10 rounded-xl text-sm font-medium hover:bg-white/20 transition-colors flex items-center gap-2"
                  >
                    <Copy className="w-3.5 h-3.5" />
                    NAOMI
                  </button>
                </div>
              </div>
              
              {/* Other tools */}
              {content.sections.what_i_used.slice(1).map((tool, idx) => (
                <ToolCard key={idx} {...tool} />
              ))}
            </div>
          </div>
        </div>
        
        {/* CTA Section */}
        <div className="px-6 py-12 bg-white text-center">
          <div className="max-w-lg mx-auto">
            <h2 className="text-xl font-bold mb-3 text-gray-900">Ready to Learn?</h2>
            <p className="text-sm text-gray-600 mb-8 leading-relaxed">
              Join Front Row and follow along as I build Phygital from scratch.
            </p>
            <Button 
              onClick={() => {
                setShowSignup(true);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className="bg-black hover:bg-gray-800 px-8 py-6 rounded-2xl text-base"
            >
              <FrontRowIcon className="w-5 h-5 mr-2" />
              Get Started Free
            </Button>
          </div>
        </div>
        
        {/* Footer */}
        <div className="px-6 py-8 border-t border-gray-100 bg-gray-50">
          <div className="max-w-lg mx-auto">
            <div className="flex items-center justify-center gap-2 text-gray-400 text-xs mb-4">
              <PhygitalIcon className="w-4 h-4" />
              <span>Powered by Phygital</span>
            </div>
            <div className="flex items-center justify-center gap-6 text-xs text-gray-400">
              <Link to="/" className="hover:text-gray-600">Home</Link>
              <Link to="/me" className="hover:text-gray-600">Product</Link>
              <Link to="/admin" className="hover:text-gray-600">Admin</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Student dashboard - Mobile First
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-gray-100">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-black rounded-lg flex items-center justify-center">
              <FrontRowIcon className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-sm">Front Row</span>
          </div>
          
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="text-xs">
              Month {currentMonth}
            </Badge>
            <button 
              onClick={() => setMobileNav(!mobileNav)}
              className="w-8 h-8 flex items-center justify-center"
            >
              {mobileNav ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
        
        {/* Mobile nav drawer */}
        {mobileNav && (
          <div className="absolute top-full left-0 right-0 bg-white border-b border-gray-100 p-4 shadow-lg">
            <div className="space-y-2">
              {[
                { id: "about", label: "About / Guide", icon: Users },
                { id: "what_i_did", label: "What I Did", icon: Target },
                { id: "what_i_used", label: "What I Used", icon: Zap },
                { id: "what_i_learned", label: "What I Learned", icon: Lightbulb },
                { id: "whats_next", label: "What's Next", icon: Rocket },
                { id: "feedback", label: "Feedback", icon: MessageSquare }
              ].map((item) => (
                <button
                  key={item.id}
                  onClick={() => { setActiveSection(item.id); setMobileNav(false); }}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left ${
                    activeSection === item.id
                      ? "bg-black text-white"
                      : "bg-gray-50 text-gray-700"
                  }`}
                >
                  <item.icon className="w-4 h-4" />
                  <span className="text-sm font-medium">{item.label}</span>
                </button>
              ))}
            </div>
            
            <div className="border-t border-gray-100 mt-4 pt-4">
              <p className="text-xs text-gray-400 mb-2">Phygital HQ</p>
              <div className="flex gap-2">
                <Link to="/me" className="text-xs text-gray-600 hover:text-black">Product</Link>
                <Link to="/admin" className="text-xs text-gray-600 hover:text-black">Admin</Link>
                <Link to="/" className="text-xs text-gray-600 hover:text-black">Home</Link>
              </div>
            </div>
          </div>
        )}
      </header>
      
      {/* Section tabs - Mobile scroll */}
      <div className="bg-white px-4 py-3 overflow-x-auto">
        <div className="flex gap-2 min-w-max">
          {[
            { id: "about", label: "Guide", icon: Users },
            { id: "my_profile", label: "My Profile", icon: Users2 },
            { id: "what_i_did", label: "Did", icon: Target },
            { id: "what_i_used", label: "Used", icon: Zap },
            { id: "what_i_learned", label: "Learned", icon: Lightbulb },
            { id: "whats_next", label: "Next", icon: Rocket },
            { id: "feedback", label: "Feedback", icon: MessageSquare }
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveSection(item.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                activeSection === item.id
                  ? "bg-black text-white"
                  : "bg-gray-100 text-gray-600"
              }`}
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </button>
          ))}
        </div>
      </div>
      
      {/* Content */}
      <div className="px-4 py-6">
        {/* Month header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">{content.title}</h1>
          <p className="text-gray-500 text-sm">{content.subtitle}</p>
        </div>
        
        {/* About/Guide Section */}
        {activeSection === "about" && (
          <div className="space-y-4">
            {/* Guide Card */}
            <Card className="border-0 shadow-md rounded-2xl overflow-hidden">
              <CardContent className="p-0">
                <div className="bg-gradient-to-br from-gray-900 to-gray-800 p-5 text-white">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-xl overflow-hidden border-2 border-white/20">
                      <img 
                        src={GUIDE_INFO.photo} 
                        alt={GUIDE_INFO.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold">{GUIDE_INFO.name}</h3>
                      <p className="text-pink-400 text-xs font-medium">{GUIDE_INFO.subtitle}</p>
                    </div>
                  </div>
                  <p className="text-gray-300 text-xs mt-3">{GUIDE_INFO.bio}</p>
                </div>
                
                {/* Journey milestones */}
                <div className="p-4 bg-white">
                  <div className="flex gap-2">
                    {GUIDE_INFO.journey.map((item, idx) => {
                      const JIcon = item.icon;
                      return (
                        <div key={idx} className="flex-1 bg-gray-50 rounded-lg p-2 text-center">
                          <JIcon className="w-4 h-4 mx-auto text-gray-600 mb-1" />
                          <p className="text-[9px] text-gray-400">{item.age}</p>
                          <p className="text-[10px] font-medium text-gray-700">{item.milestone}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* AI Perspectives */}
            <div className="space-y-3">
              <h4 className="font-semibold text-gray-900 text-sm">My Perspective on AI</h4>
              {GUIDE_INFO.perspectives.map((item, idx) => (
                <div key={idx} className={`p-4 rounded-xl ${idx === 0 ? 'bg-blue-50 border-l-4 border-blue-500' : 'bg-pink-50 border-l-4 border-pink-500'}`}>
                  <h5 className="font-semibold text-gray-900 text-sm mb-1">{item.title}</h5>
                  <p className="text-xs text-gray-600">{item.text}</p>
                </div>
              ))}
            </div>
            
            {/* About Front Row */}
            <Card className="bg-gradient-to-br from-black to-gray-800 text-white rounded-2xl">
              <CardContent className="p-5">
                <h4 className="font-bold text-lg mb-2">About Front Row</h4>
                <p className="text-gray-300 text-sm mb-4">
                  A real-time documentary of building a tech startup. No fluff, no gatekeeping, just transparent, month-by-month progress.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-white/10 rounded-xl p-3 text-center">
                    <p className="text-2xl font-bold">12</p>
                    <p className="text-xs text-gray-400">Months Planned</p>
                  </div>
                  <div className="bg-white/10 rounded-xl p-3 text-center">
                    <p className="text-2xl font-bold">Free</p>
                    <p className="text-xs text-gray-400">To Join</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
        
        {/* Timeline - Visual */}
        {activeSection === "what_i_did" && (
          <div className="mb-6">
            <div className="flex gap-1 overflow-x-auto pb-2">
              {content.timeline.map((item, idx) => (
                <div 
                  key={idx} 
                  className={`flex-shrink-0 px-3 py-2 rounded-lg text-xs ${
                    item.type === 'discovery' ? 'bg-pink-100 text-pink-700' :
                    item.type === 'research' ? 'bg-blue-100 text-blue-700' :
                    item.type === 'build' ? 'bg-purple-100 text-purple-700' :
                    item.type === 'business' ? 'bg-orange-100 text-orange-700' :
                    item.type === 'launch' ? 'bg-green-100 text-green-700' :
                    'bg-gray-100 text-gray-700'
                  }`}
                >
                  <div className="font-medium">{item.date}</div>
                  <div className="text-[10px] opacity-80">{item.event}</div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* My Profile Section - Provider Intake Management */}
        {activeSection === "my_profile" && (
          <ProviderProfileSection 
            studentData={studentData}
            onUpdate={(updatedData) => setStudentData(updatedData)}
          />
        )}
        
        {/* What I Did */}
        {activeSection === "what_i_did" && (
          <div className="space-y-3">
            {content.sections.what_i_did.map((item, idx) => (
              <div key={idx} className="bg-white p-4 rounded-2xl">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Check className="w-3 h-3 text-green-600" />
                  </div>
                  <div>
                    <p className="text-gray-900 font-medium text-sm">{item.text}</p>
                    {item.detail && <p className="text-xs text-gray-500 mt-1">{item.detail}</p>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* What I Used - App style cards */}
        {activeSection === "what_i_used" && (
          <div className="space-y-3">
            <p className="text-xs text-gray-500 mb-4">
              Tools I actually use. Affiliate codes where I have them - you get a discount, I get credit. Win-win! 🤝
            </p>
            
            {/* Featured: AI Dev Tools */}
            <div className="bg-gradient-to-r from-black to-gray-800 rounded-2xl p-4 text-white mb-4">
              <div className="flex items-center gap-3 mb-3">
                <img 
                  src="https://img.icons8.com/fluency/48/artificial-intelligence.png" 
                  alt="AI Dev Tools" 
                  className="w-10 h-10 rounded-xl bg-white p-1"
                />
                <div className="flex-1">
                  <h4 className="font-bold text-sm">AI Dev Tools</h4>
                  <p className="text-[10px] text-gray-300">AI-powered development</p>
                </div>
              </div>
              <div className="flex gap-2">
                <a 
                  href="#"
                  className="flex-1 bg-white text-black text-center py-2 rounded-xl text-xs font-medium"
                >
                  Learn More →
                </a>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText("NAOMI");
                    toast.success("Code NAOMI copied!");
                  }}
                  className="px-3 py-2 bg-white/10 rounded-xl text-xs font-medium flex items-center gap-1"
                >
                  <Copy className="w-3 h-3" />
                  NAOMI
                </button>
              </div>
            </div>
            
            {content.sections.what_i_used.slice(1).map((tool, idx) => (
              <ToolCard key={idx} {...tool} />
            ))}
          </div>
        )}
        
        {/* What I Learned */}
        {activeSection === "what_i_learned" && (
          <div className="space-y-3">
            {content.sections.what_i_learned.map((lesson, idx) => (
              <div key={idx} className="bg-white p-4 rounded-2xl border-l-4 border-pink-500">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-pink-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-pink-600 font-bold text-sm">{idx + 1}</span>
                  </div>
                  <p className="text-gray-700 text-sm">{lesson}</p>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* What's Next */}
        {activeSection === "whats_next" && (
          <div className="space-y-3">
            {content.sections.whats_next.map((item, idx) => (
              <div key={idx} className="bg-white p-4 rounded-2xl flex items-center gap-3">
                <Circle className="w-4 h-4 text-gray-300" />
                <span className="text-gray-700 text-sm">{item}</span>
              </div>
            ))}
          </div>
        )}
        
        {/* Feedback */}
        {activeSection === "feedback" && (
          <div className="space-y-4">
            <div className="bg-white p-4 rounded-2xl space-y-3">
              {content.feedback_questions.map((q, idx) => (
                <div key={idx} className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 rounded-xl p-3">
                  <span className="text-pink-500">•</span>
                  {q}
                </div>
              ))}
            </div>
            
            <Textarea
              placeholder="Share your thoughts..."
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              className="min-h-[120px] rounded-2xl border-gray-200"
              data-testid="frontrow-feedback-input"
            />
            
            {/* File Attachments Section */}
            <div className="bg-white p-4 rounded-2xl space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-gray-700">Attachments (optional)</p>
                <label className="cursor-pointer">
                  <input
                    type="file"
                    multiple
                    accept="image/*,application/pdf,.doc,.docx"
                    onChange={handleFeedbackFileUpload}
                    className="hidden"
                    data-testid="feedback-file-input"
                  />
                  <span className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg transition-colors ${
                    uploadingFile 
                      ? "bg-gray-100 text-gray-400" 
                      : "bg-pink-50 text-pink-600 hover:bg-pink-100"
                  }`}>
                    {uploadingFile ? (
                      <div className="w-3 h-3 border-2 border-pink-400 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                    )}
                    {uploadingFile ? "Uploading..." : "Add Files"}
                  </span>
                </label>
              </div>
              
              {feedbackAttachments.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {feedbackAttachments.map((file) => (
                    <div 
                      key={file.attachment_id}
                      className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg text-sm"
                    >
                      <FileText className="w-4 h-4 text-gray-400" />
                      <span className="truncate max-w-[150px]">{file.filename}</span>
                      <button
                        onClick={() => removeFeedbackAttachment(file.attachment_id)}
                        className="text-gray-400 hover:text-red-500 ml-1"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-xs text-gray-400">
                Attach screenshots, mockups, or documents. Max 10MB per file.
              </p>
            </div>
            
            <Button 
              onClick={handleSubmitFeedback}
              disabled={submitting || !feedback.trim()}
              className="w-full bg-black hover:bg-gray-800 h-12 rounded-2xl"
              data-testid="frontrow-submit-btn"
            >
              {submitting ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Submit & Unlock Next Month
                </>
              )}
            </Button>
          </div>
        )}
      </div>
      
      {/* Bottom bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <PhygitalIcon className="w-5 h-5 text-gray-400" />
          <span className="text-xs text-gray-400">Powered by Phygital</span>
        </div>
        <div className="flex gap-1">
          {[1, 2, 3].map((month) => (
            <button
              key={month}
              onClick={() => month <= (studentData?.current_month_unlocked || 1) && setCurrentMonth(month)}
              disabled={month > (studentData?.current_month_unlocked || 1)}
              className={`w-8 h-8 rounded-full text-xs font-medium ${
                currentMonth === month 
                  ? "bg-black text-white" 
                  : month <= (studentData?.current_month_unlocked || 1)
                    ? "bg-gray-100 text-gray-700"
                    : "bg-gray-50 text-gray-300"
              }`}
            >
              {month > (studentData?.current_month_unlocked || 1) ? <Lock className="w-3 h-3 mx-auto" /> : month}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FrontRowPage;
FrontRowPage;
